import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import type { TimesheetDeadlineSettings } from '@/types/timesheetSettings';

interface ApprovalReminderSettingsProps {
  settings: TimesheetDeadlineSettings;
  onChange: (settings: TimesheetDeadlineSettings, isValid: boolean) => void;
}

export const ApprovalReminderSettings: React.FC<ApprovalReminderSettingsProps> = ({
  settings,
  onChange,
}) => {
  const enableApprovalReminders = settings.enableApprovalReminders ?? false;
  const approvalReminderDaysThreshold = settings.approvalReminderDaysThreshold ?? 2;

  const isThresholdValid =
    Number.isInteger(approvalReminderDaysThreshold) &&
    approvalReminderDaysThreshold >= 1 &&
    approvalReminderDaysThreshold <= 14;

  const handleToggleChange = (checked: boolean) => {
    const newSettings: TimesheetDeadlineSettings = {
      ...settings,
      enableApprovalReminders: checked,
    };
    // When toggling, validity depends on whether the input is shown and valid
    const valid = !checked || isThresholdValid;
    onChange(newSettings, valid);
  };

  const handleThresholdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const parsed = parseInt(raw, 10);
    const newSettings: TimesheetDeadlineSettings = {
      ...settings,
      approvalReminderDaysThreshold: isNaN(parsed) ? 0 : parsed,
    };
    const valid = Number.isInteger(parsed) && parsed >= 1 && parsed <= 14;
    onChange(newSettings, valid);
  };

  const showThresholdInput = enableApprovalReminders;
  const showThresholdError = showThresholdInput && !isThresholdValid;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Approval Reminders</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">

        {/* Enable Approval Reminders toggle */}
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="enableApprovalReminders">Approval Reminder Notifications</Label>
            <p className="text-sm text-muted-foreground">
              Enable feed notifications to approvers with outstanding timesheets
            </p>
          </div>
          <Switch
            id="enableApprovalReminders"
            checked={enableApprovalReminders}
            onCheckedChange={handleToggleChange}
          />
        </div>

        {/* Threshold input — only shown when toggle is on */}
        {showThresholdInput && (
          <div className="pl-4 border-l-2 border-muted space-y-2">
            <Label htmlFor="approvalReminderDaysThreshold">
              Send reminder after a timesheet has been waiting for
            </Label>
            <div className="flex gap-2">
              <Input
                id="approvalReminderDaysThreshold"
                type="number"
                min={1}
                max={14}
                value={approvalReminderDaysThreshold}
                onChange={handleThresholdChange}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    const next = Math.min(14, approvalReminderDaysThreshold + 1);
                    handleThresholdChange({ target: { value: String(next) } } as React.ChangeEvent<HTMLInputElement>);
                  }
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    const next = Math.max(1, approvalReminderDaysThreshold - 1);
                    handleThresholdChange({ target: { value: String(next) } } as React.ChangeEvent<HTMLInputElement>);
                  }
                }}
                className={`h-10 w-20 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none${showThresholdError ? ' border-destructive focus-visible:ring-destructive' : ''}`}
                aria-describedby={showThresholdError ? 'approval-threshold-error' : 'approval-threshold-hint'}
                aria-invalid={showThresholdError}
              />
              <div className="flex items-center px-3 text-sm text-muted-foreground whitespace-nowrap">
                days
              </div>
            </div>

            {showThresholdError ? (
              <p id="approval-threshold-error" className="text-xs text-destructive">
                Please enter a value between 1 and 14.
              </p>
            ) : (
              <p id="approval-threshold-hint" className="text-xs text-muted-foreground">
                How many days a timesheet can be waiting before sending a reminder
              </p>
            )}

            {/* Recipient clarification */}
            <p className="text-sm text-muted-foreground">
              Reminders are sent to the assigned approver only.
            </p>
          </div>
        )}

      </CardContent>
    </Card>
  );
};
